import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const GeographyTreasureHuntGame = ({ gameData, onComplete }) => {
    const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 

    const currentLocation = gameData?.locations?.[currentLocationIndex];

    const handleOptionSelect = (option) => {
        if (selectedOption || feedback) return;
        triggerHaptic(30);
        setSelectedOption(option);
        
        const isCorrectResult = option === currentLocation.name;
        if (isCorrectResult) {
            const newScore = score + (gameData.points_per_location || 20);
            setScore(newScore);
            triggerHaptic([100, 50, 100]);
        } else {
            triggerHaptic([50, 50, 50]);
        }

        setFeedback({ isCorrect: isCorrectResult, fact: currentLocation.fact, option: option });

        setTimeout(() => {
            setFeedback(null);
            if (currentLocationIndex < gameData.locations.length - 1) {
                setCurrentLocationIndex(prev => prev + 1);
                setSelectedOption(null);
            } else {
                triggerHaptic([200, 100, 200, 100, 300]);
                onComplete?.(score + (isCorrectResult ? (gameData.points_per_location || 20) : 0));
            }
        }, 5000);
    };

    if (!gameData || !gameData.locations) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-blue-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🧭</div>
                <h3 className="text-base md:text-3xl font-black text-blue-900 tracking-tighter uppercase">Ruošiamas žemėlapis...</h3>
            </div>
        );
    }

    const progress = (currentLocationIndex / gameData.locations.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-blue-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🧭
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Geografijos <span className="text-blue-600">Lobiai</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Atradimai</p>
                        <p className="text-sm md:text-2xl font-black text-blue-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Stotelė</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentLocationIndex + 1}/{gameData.locations.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-blue-400 via-teal-500 to-emerald-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center justify-center shadow-2xl">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none">🌍</div>
                <h4 className="text-[8px] md:text-[10px] font-black text-blue-400 uppercase tracking-[0.5em] mb-6 md:mb-10">Ekspedicijos Užuomina</h4>
                
                <div className="text-8xl md:text-[10rem] mb-6 md:mb-12 drop-shadow-2xl animate-float">
                    {currentLocation.emoji}
                </div>
                
                <div className="bg-white rounded-xl md:rounded-[3rem] p-6 md:p-12 shadow-inner border-2 border-blue-50 relative group w-full max-w-3xl">
                    <p className="text-sm md:text-3xl font-black text-slate-800 leading-tight italic font-serif px-4">
                        "{currentLocation.clue}"
                    </p>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto py-4">
            <div className="flex items-center gap-2 px-4 self-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Pasirink Vietovę</h4>
            </div>
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-8 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden w-full min-h-[400px] flex flex-col justify-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 relative z-10 p-2 md:p-6">
                    {currentLocation.options.map((option, index) => {
                        const isSelected = selectedOption === option;
                        const isCorrectInFeed = feedback?.isCorrect && isSelected;
                        const isWrongInFeed = feedback && !feedback.isCorrect && isSelected;
                        const isCorrectOther = feedback && option === currentLocation.name && !isSelected;

                        return (
                            <button
                                key={index}
                                onClick={() => handleOptionSelect(option)}
                                disabled={!!selectedOption}
                                className={`
                                    premium-button-3d relative rounded-xl md:rounded-[2.5rem] p-4 md:p-8 flex items-center justify-center text-center transition-all duration-300 transform border-b-8 md:border-b-[12px]
                                    ${isCorrectInFeed
                                        ? 'bg-emerald-500 text-white border-emerald-700 scale-105'
                                        : isWrongInFeed
                                            ? 'bg-rose-500 text-white border-rose-700 animate-shake'
                                            : isCorrectOther
                                                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                                                : 'bg-slate-800 text-white border-slate-700 hover:border-blue-500/50 active:translate-y-1 active:border-b-0'}
                                `}
                            >
                                <span className="text-sm md:text-2xl font-black uppercase tracking-widest">{option}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[12px] md:border-[16px]
                        ${feedback.isCorrect ? 'border-emerald-500' : 'border-blue-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback.isCorrect ? currentLocation.emoji : '🧭'}
                        </div>
                        
                        <div className="text-center space-y-6 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback.isCorrect ? 'text-emerald-600' : 'text-blue-600'}`}>
                                {feedback.isCorrect ? 'TIKSLUS ATRADIMAS!' : 'EKSPEDICIJOS ŽINIA'}
                            </h2>

                            <div className={`p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-4 text-center italic font-serif leading-relaxed ${feedback.isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed tracking-tight">
                                    "{feedback.fact}"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GeographyTreasureHuntGame;
